import { SerialCapturePanel } from './SerialCapturePanel';
import { SerialSelectPanel } from './SerialSelectPanel';
import type { SerialRow, SerialStatus } from '../adapter/MasterDocumentAdapter.types';

export interface SerialCaptureModeProps {
  mode: 'capture';
  /** Códigos de serial capturados. */
  serials: string[];
  required: number;
  readOnly?: boolean;
  /** Error externo (p.ej. `adapter.validateSerial`). */
  externalError?: string | null;
  /** Hay un `adapter.validateSerial` en vuelo para el último código leído. */
  validating?: boolean;
  onChange: (next: string[]) => void;
  className?: string;
}

export interface SerialSelectModeProps {
  mode: 'select';
  /** Seriales disponibles (paginados por el adapter en el kit final). */
  rows: SerialRow[];
  /** IDs seleccionados. */
  value: string[];
  required: number;
  readOnly?: boolean;
  pageSize?: number;
  onChange: (next: string[]) => void;
  className?: string;
  // ── Modo servidor (cableado vía useSerialPanel) ────────────
  /** Búsqueda controlada server-side. */
  query?: string;
  onQueryChange?: (query: string) => void;
  /** Página actual (1-based) controlada server-side. */
  page?: number;
  onPageChange?: (page: number) => void;
  /** Total de registros del lado servidor. */
  total?: number;
  /** Indicador de carga de la página. */
  loading?: boolean;
  // ── Confirmación al marcar (cableada vía useSerialPanel) ───
  /**
   * Estados que exigen confirmación Sí/No al MARCAR una fila. Ausente o vacío ⇒
   * comportamiento idéntico al actual.
   */
  confirmOnStatuses?: SerialStatus[];
  /** Clave i18n del mensaje de esa confirmación. Recibe `{serialCode}`. */
  confirmMessageKey?: string;
}

export type SerialPanelProps = SerialCaptureModeProps | SerialSelectModeProps;

/**
 * Panel nativo de seriales del kit. Selecciona la UI según `mode`:
 * - `capture` → entrada por escáner/teclado ([[SerialCapturePanel]]).
 * - `select`  → selección desde inventario ([[SerialSelectPanel]]).
 *
 * Componente presentacional. La activación condicional (`_itemIsSerialized`),
 * la lectura de la cantidad requerida y el cableado del adapter los resuelve el
 * contenedor (`LineFormModal` + hook `useSerialPanel`, paso 2).
 */
export function SerialPanel(props: SerialPanelProps) {
  if (props.mode === 'capture') {
    return (
      <SerialCapturePanel
        serials={props.serials}
        required={props.required}
        readOnly={props.readOnly}
        externalError={props.externalError}
        validating={props.validating}
        onChange={props.onChange}
        className={props.className}
      />
    );
  }
  return (
    <SerialSelectPanel
      rows={props.rows}
      value={props.value}
      required={props.required}
      readOnly={props.readOnly}
      pageSize={props.pageSize}
      onChange={props.onChange}
      className={props.className}
      query={props.query}
      onQueryChange={props.onQueryChange}
      page={props.page}
      onPageChange={props.onPageChange}
      total={props.total}
      loading={props.loading}
      confirmOnStatuses={props.confirmOnStatuses}
      confirmMessageKey={props.confirmMessageKey}
    />
  );
}
